import { z } from "zod"

import { MARKET_ALL } from "~/utils/demandScope"
import { ExtraDataTable, getExtraDataDisplayName } from "~/utils/dynamicTableUtils"

/**
 * The demand hierarchy vocabulary, React-free so the server reads `Company.demandHierarchies`
 * itself rather than trusting the client. Sales-planning levels split one variant's demand (cube
 * axes, below the variant); every other key groups variants (above it).
 */

/**
 * A row carrying every planning level's value. Each registry `field` must be a key here, so adding
 * a level fails the build until the scan selects its column.
 */
export type PlanningLevelRow = {
  warehouseId: string
  channel: string
  marketId: string
  category: string | null
  brand: string | null
  /**
   * `Sku.extraData`, read by the namespaced levels. Never a registry `field` (it would read a JSON
   * blob as one value). Optional: a carrier, absent when no extraData level is configured.
   */
  extraData?: Record<string, string | null> | null
}

type PlanningLevelKind = "cube" | "attribute"

type PlanningLevelDef = {
  label: string
  /**
   * `cube` — a real axis of the variant's cube (`isSalesPlanningLevel`), so it splits ONE variant's demand.
   * `attribute` — a single-valued variant property, so it partitions the variants instead.
   */
  kind: PlanningLevelKind
  field: Exclude<keyof PlanningLevelRow, "extraData">
}

/**
 * Every level `Company.demandHierarchies` may name: label, which side of the variant, and which
 * column carries its value. Declaration order is the picker's (grouping levels first). Adding a
 * level also touches `PlanningLevelRow` and the SQL in `hierarchyInsights.ts`.
 */
export const PLANNING_LEVELS = {
  category: { label: "Category", kind: "attribute", field: "category" },
  brand: { label: "Brand", kind: "attribute", field: "brand" },
  warehouseId: { label: "Warehouse", kind: "cube", field: "warehouseId" },
  channel: { label: "Channel", kind: "cube", field: "channel" },
  marketId: { label: "Market", kind: "cube", field: "marketId" },
} as const satisfies Record<string, PlanningLevelDef>

type PlanningLevelKey = keyof typeof PLANNING_LEVELS

type KeysOfKind<K extends PlanningLevelKind> = {
  [P in PlanningLevelKey]: (typeof PLANNING_LEVELS)[P]["kind"] extends K ? P : never
}[PlanningLevelKey]

export type DemandSalesPlanningLevel = KeysOfKind<"cube">
export type DemandGroupingLevel = KeysOfKind<"attribute">

/** The row properties grouping levels read. A variant carries these; its cube axes are collapsed. */
type GroupingLevelField = (typeof PLANNING_LEVELS)[DemandGroupingLevel]["field"]

const keysOfKind = <K extends PlanningLevelKind>(kind: K) =>
  (Object.keys(PLANNING_LEVELS) as PlanningLevelKey[]).filter(
    (k) => PLANNING_LEVELS[k].kind === kind,
  ) as KeysOfKind<K>[]

export const DEMAND_SALES_PLANNING_LEVELS = keysOfKind("cube")
export const DEMAND_GROUPING_LEVELS = keysOfKind("attribute")

/** Whether this build knows the level at all — `updateDemandSettings` validates only `z.string()`. */
export const isPlanningLevel = (value: string): value is PlanningLevelKey =>
  Object.hasOwn(PLANNING_LEVELS, value)

/**
 * `Sku.extraData` fields are per-tenant, so not registry entries, but they group variants like
 * `category`. Namespaced (unlike budget's bare-name fallback) so an unknown unprefixed key stays a
 * hard error instead of collapsing the collection into one uncategorised node.
 */
const EXTRA_DATA_PREFIX = "extraData:"

export type ExtraDataLevelKey = `${typeof EXTRA_DATA_PREFIX}${string}`

/** Every level key this build can actually read: a registry entry, or a tenant's own extraData field. */
export type DemandLevelKey = PlanningLevelKey | ExtraDataLevelKey

export const extraDataLevelKey = (fieldName: string): ExtraDataLevelKey =>
  `${EXTRA_DATA_PREFIX}${fieldName}`

export const isExtraDataLevel = (value: string): value is ExtraDataLevelKey =>
  value.startsWith(EXTRA_DATA_PREFIX) && value.length > EXTRA_DATA_PREFIX.length

/** The `Sku.extraData` key the level reads. Takes the narrowed type, so there is no null to unwrap. */
export const extraDataFieldOf = (levelKey: ExtraDataLevelKey): string =>
  levelKey.slice(EXTRA_DATA_PREFIX.length)

/**
 * Whether the level is readable. `isPlanningLevel` stays registry-only so the total `Record`s keyed
 * on it keep failing the build when a level is added.
 */
export const isDemandLevel = (value: string): value is DemandLevelKey =>
  isPlanningLevel(value) || isExtraDataLevel(value)

export function isSalesPlanningLevel(value: string): value is DemandSalesPlanningLevel {
  return isPlanningLevel(value) && PLANNING_LEVELS[value].kind === "cube"
}

/** Every sales-planning axis: the configured ones in hierarchy order, the rest after in default order. */
export const salesAxesInHierarchyOrder = (
  levels: readonly string[],
): DemandSalesPlanningLevel[] => {
  const configured = levels.filter(isSalesPlanningLevel)
  return [...configured, ...DEMAND_SALES_PLANNING_LEVELS.filter((a) => !configured.includes(a))]
}

export function isGroupingLevel(value: string): value is DemandGroupingLevel {
  return isPlanningLevel(value) && PLANNING_LEVELS[value].kind === "attribute"
}

/** A level that partitions the assortment: a registry attribute, or a tenant's own extraData field. */
export type AssortmentLevelKey = DemandGroupingLevel | ExtraDataLevelKey

/**
 * Whether the level partitions the assortment: a single-valued product property (`Sku` column or
 * extraData field), so each variant is in exactly one node. Cube axes
 * ({@link isSalesPlanningLevel}) have no assortment node, so assortment surfaces (`FramingView`,
 * `framing.ts`) accept only these. Unknown keys fail both and are dropped.
 */
export const isAssortmentLevel = (value: string): value is AssortmentLevelKey =>
  isGroupingLevel(value) || isExtraDataLevel(value)

/**
 * extraData levels are named by `getExtraDataDisplayName`, as every table names them. The tenant's
 * `userSetDisplayName` needs a client read, so `useLevelLabel` passes it to the same helper.
 */
export const levelLabel = (value: string): string => {
  if (isPlanningLevel(value)) return PLANNING_LEVELS[value].label
  if (!isExtraDataLevel(value)) return value
  return getExtraDataDisplayName(extraDataFieldOf(value), ExtraDataTable.SKU)
}

/**
 * This level's value on a row, as a node key. `""` = no value: the uncategorised bucket, whose
 * demand is counted, not dropped.
 */
export const levelValueOf = (row: PlanningLevelRow, levelKey: DemandLevelKey): string => {
  if (isExtraDataLevel(levelKey)) return row.extraData?.[extraDataFieldOf(levelKey)] ?? ""
  const raw = row[PLANNING_LEVELS[levelKey].field]
  if (raw == null) return ""
  return typeof raw === "number" ? (raw === MARKET_ALL ? "" : String(raw)) : raw
}

export const UNCATEGORIZED = "Uncategorized"

// A variant carries the grouping levels' fields only — its cube axes are collapsed by the time a
// per-variant total exists.
type GroupableVariant = { [K in GroupingLevelField]?: string | null } & {
  /**
   * `Sku.extraData`, for the namespaced levels. `unknown` because the column is Prisma `Json` and arrives
   * that way — narrowed at the read below rather than asserted here, which is the only place that knows a
   * usable value from a nested object somebody ingested.
   */
  extraData?: unknown
}

/**
 * How a variant-grouping level reads its value off one variant, off `PLANNING_LEVELS` so there is no second
 * list of which levels are readable. An unrecognised level must stay DETECTABLE rather than reading
 * `undefined` for every variant: that buckets them all into UNCATEGORIZED, rendering one node that holds the
 * whole collection as though it were a real breakdown. `Company.demandHierarchies` accepts any string, so
 * callers check `isDemandLevel` (or `isGroupingLevel`) first.
 */
export const groupingAttributeOf = (
  v: GroupableVariant,
  levelKey: string,
): string | null | undefined => {
  if (isExtraDataLevel(levelKey)) {
    const blob = v.extraData
    if (blob == null || typeof blob !== "object") return null
    const raw = (blob as Record<string, unknown>)[extraDataFieldOf(levelKey)]
    // Only scalars are node keys. An empty string is no value rather than a value named "", so it buckets
    // with the missing ones — a field ingested with blanks would otherwise render an unlabelled node
    // beside the uncategorised one, and the two mean the same thing.
    if (raw == null || raw === "" || typeof raw === "object") return null
    return String(raw)
  }
  return isGroupingLevel(levelKey) ? v[PLANNING_LEVELS[levelKey].field] : undefined
}

const DemandHierarchySchema = z.array(z.object({ level: z.number(), value: z.string().nullable() }))

/**
 * `Company.demandHierarchies` → the ordered level keys; `null` levels are dropped, so empty = the
 * variant total. Unparseable config yields empty rather than throwing.
 */
export function parseDemandHierarchy(raw: unknown): string[] {
  const parsed = DemandHierarchySchema.safeParse(raw)
  if (!parsed.success) return []
  return parsed.data
    .filter((l) => l.value !== null)
    .sort((a, b) => a.level - b.level)
    .map((l) => l.value as string)
}
